import { TFunction } from 'i18next';
import type { GettingStartedStepKey } from '@/types/aiChat';

/**
 * Quick prompt shown in Gigawatt chat when there is no conversation yet.
 * Page-specific prompts are resolved by getQuickPromptsForPage(pageId, t).
 */
export type QuickPrompt = {
  id?: string;
  label: string;
  message: string;
};

const MAX_QUICK_PROMPTS = 4;
const MAX_GS_QUICK_PROMPTS = 3;

/**
 * Returns a random subset of size up to `count` from `arr` (shuffle then take first `count`).
 * Uses Fisher–Yates so each visit can show a different set of 4 for traction measurement.
 */
export function pickRandomSubset<T>(arr: T[], count: number): T[] {
  if (arr.length <= count) return [...arr];
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
}

/**
 * Builds QuickPrompt entries from a page's translation key prefix and a list of
 * { id, leafKey } pairs. The translated string is used for both label and message.
 */
function buildPrompts(
  t: TFunction<'ai-chat'>,
  pagePrefix: string,
  entries: { id: string; leafKey: string }[]
): QuickPrompt[] {
  return entries.map(({ id, leafKey }) => {
    const message = t(`quickPrompts.${pagePrefix}.${leafKey}`);
    return { id, label: message, message };
  });
}

/**
 * Returns quick prompts for the given page identifier.
 * Page identifiers come from getSemanticPageIdentifier(path) (e.g. "dashboard", "people_detail").
 * At most MAX_QUICK_PROMPTS (4) should be shown; the hook uses pickRandomSubset when there are more.
 */
export function getQuickPromptsForPage(
  pageId: string,
  t: TFunction<'ai-chat'>
): QuickPrompt[] {
  switch (pageId) {
    case 'dashboard':
      return buildPrompts(t, 'dashboard', [
        { id: 'dashboard-open-tasks', leafKey: 'openTasks' },
        {
          id: 'dashboard-employees-need-devices',
          leafKey: 'employeesNeedDevices',
        },
        { id: 'dashboard-devices-not-mdm', leafKey: 'devicesNotMdm' },
        {
          id: 'dashboard-onboardings-in-progress',
          leafKey: 'onboardingsInProgress',
        },
        { id: 'dashboard-password-breaches', leafKey: 'passwordBreaches' },
        { id: 'dashboard-orders-in-progress', leafKey: 'ordersInProgress' },
        { id: 'dashboard-devices-in-storage', leafKey: 'devicesInStorage' },
        { id: 'dashboard-security-posture', leafKey: 'securityPosture' },
        {
          id: 'dashboard-electric-org-benefits',
          leafKey: 'electricOrgBenefits',
        },
      ]);
    case 'assets':
      return buildPrompts(t, 'assets', [
        { id: 'assets-show-mine', leafKey: 'showMine' },
        { id: 'assets-available', leafKey: 'available' },
        { id: 'assets-need-repair', leafKey: 'needRepair' },
        { id: 'assets-by-warranty', leafKey: 'byWarranty' },
        { id: 'assets-missing', leafKey: 'missing' },
        { id: 'assets-how-to-add', leafKey: 'howToAdd' },
        { id: 'assets-in-transit', leafKey: 'inTransit' },
      ]);
    case 'active_employees':
      return buildPrompts(t, 'activeEmployees', [
        { id: 'active-employees-show-mine', leafKey: 'showMine' },
        { id: 'active-employees-add', leafKey: 'add' },
        {
          id: 'active-employees-start-onboarding',
          leafKey: 'startOnboarding',
        },
        {
          id: 'active-employees-start-offboarding',
          leafKey: 'startOffboarding',
        },
        { id: 'active-employees-have-devices', leafKey: 'haveDevices' },
        { id: 'active-employees-have-mdm', leafKey: 'haveMdm' },
        { id: 'active-employees-add-via-hris', leafKey: 'addViaHris' },
      ]);
    case 'people':
      return buildPrompts(t, 'people', [
        { id: 'people-show-my-employees', leafKey: 'showMyEmployees' },
        { id: 'people-add-employee', leafKey: 'addEmployee' },
        { id: 'people-start-onboarding', leafKey: 'startOnboarding' },
        { id: 'people-start-offboarding', leafKey: 'startOffboarding' },
        { id: 'people-have-devices', leafKey: 'haveDevices' },
        { id: 'people-have-mdm', leafKey: 'haveMdm' },
        { id: 'people-add-via-hris', leafKey: 'addViaHris' },
      ]);
    case 'inactive_employees':
      return buildPrompts(t, 'inactiveEmployees', [
        { id: 'inactive-show-list', leafKey: 'showList' },
        { id: 'inactive-recent-offboardings', leafKey: 'recentOffboardings' },
        { id: 'inactive-reactivate-license', leafKey: 'reactivateLicense' },
        {
          id: 'inactive-offboarded-have-devices',
          leafKey: 'offboardedHaveDevices',
        },
        { id: 'inactive-what-page-shows', leafKey: 'whatPageShows' },
        { id: 'inactive-how-to-offboard', leafKey: 'howToOffboard' },
      ]);
    case 'groups':
      return buildPrompts(t, 'groups', [
        { id: 'groups-show-mine', leafKey: 'showMine' },
        { id: 'groups-how-to-create', leafKey: 'howToCreate' },
        { id: 'groups-apps-per-group', leafKey: 'appsPerGroup' },
        { id: 'groups-add-employees', leafKey: 'addEmployees' },
        { id: 'groups-parent-groups', leafKey: 'parentGroups' },
        { id: 'groups-synced-from-hr', leafKey: 'syncedFromHr' },
        { id: 'groups-how-to-delete', leafKey: 'howToDelete' },
      ]);
    case 'applications':
      return buildPrompts(t, 'applications', [
        { id: 'applications-show-mine', leafKey: 'showMine' },
        { id: 'applications-how-to-add', leafKey: 'howToAdd' },
        { id: 'applications-automate', leafKey: 'automate' },
        {
          id: 'applications-connect-integration',
          leafKey: 'connectIntegration',
        },
        { id: 'applications-champions', leafKey: 'champions' },
        { id: 'applications-by-connection', leafKey: 'byConnection' },
        {
          id: 'applications-automation-support',
          leafKey: 'automationSupport',
        },
      ]);
    case 'support_center':
      return buildPrompts(t, 'supportCenter', [
        { id: 'support-submit-ticket', leafKey: 'submitTicket' },
        { id: 'support-ticket-champions', leafKey: 'ticketChampions' },
        { id: 'support-add-champions', leafKey: 'addChampions' },
        { id: 'support-ticket-categories', leafKey: 'ticketCategories' },
        { id: 'support-contact-electric', leafKey: 'contactElectric' },
        { id: 'support-what-page-shows', leafKey: 'whatPageShows' },
      ]);
    case 'tasks':
      return buildPrompts(t, 'tasks', [
        { id: 'tasks-open-tasks', leafKey: 'openTasks' },
        { id: 'tasks-all-company-tasks', leafKey: 'allCompanyTasks' },
        { id: 'tasks-overdue', leafKey: 'overdue' },
        { id: 'tasks-reassign', leafKey: 'reassign' },
        { id: 'tasks-champions', leafKey: 'champions' },
        { id: 'tasks-mark-complete', leafKey: 'markComplete' },
        { id: 'tasks-by-category', leafKey: 'byCategory' },
      ]);
    case 'device_management':
      return buildPrompts(t, 'deviceManagement', [
        { id: 'device-mgmt-devices-at-risk', leafKey: 'devicesAtRisk' },
        { id: 'device-mgmt-unmanaged', leafKey: 'unmanaged' },
        { id: 'device-mgmt-end-of-life', leafKey: 'endOfLife' },
        { id: 'device-mgmt-near-end-of-life', leafKey: 'nearEndOfLife' },
        { id: 'device-mgmt-unassigned', leafKey: 'unassigned' },
        {
          id: 'device-mgmt-default-mdm-policies',
          leafKey: 'defaultMdmPolicies',
        },
        { id: 'device-mgmt-non-reporting', leafKey: 'nonReporting' },
      ]);
    case 'antivirus':
      return buildPrompts(t, 'antivirus', [
        { id: 'antivirus-what-page-shows', leafKey: 'whatPageShows' },
        { id: 'antivirus-blocked-threats', leafKey: 'blockedThreats' },
        { id: 'antivirus-devices-registered', leafKey: 'devicesRegistered' },
        { id: 'antivirus-scanned-recently', leafKey: 'scannedRecently' },
        {
          id: 'antivirus-devices-need-reboot',
          leafKey: 'devicesNeedReboot',
        },
        { id: 'antivirus-not-scanned', leafKey: 'notScanned' },
        { id: 'antivirus-what-is-threatdown', leafKey: 'whatIsThreatdown' },
      ]);
    case 'password_breaches':
      return buildPrompts(t, 'passwordBreaches', [
        {
          id: 'password-breaches-what-page-shows',
          leafKey: 'whatPageShows',
        },
        {
          id: 'password-breaches-employees-breached',
          leafKey: 'employeesBreached',
        },
        { id: 'password-breaches-recent', leafKey: 'recent' },
        { id: 'password-breaches-by-date', leafKey: 'byDate' },
        { id: 'password-breaches-what-to-do', leafKey: 'whatToDo' },
        {
          id: 'password-breaches-have-i-been-pwned',
          leafKey: 'haveIBeenPwned',
        },
      ]);
    case 'email_security':
      return buildPrompts(t, 'emailSecurity', [
        { id: 'email-security-what-page-shows', leafKey: 'whatPageShows' },
        { id: 'email-security-threats-by-type', leafKey: 'threatsByType' },
        {
          id: 'email-security-threats-by-severity',
          leafKey: 'threatsBySeverity',
        },
        {
          id: 'email-security-employees-targeted',
          leafKey: 'employeesTargeted',
        },
        {
          id: 'email-security-threats-scanned',
          leafKey: 'threatsScanned',
        },
        { id: 'email-security-what-is-harmony', leafKey: 'whatIsHarmony' },
      ]);
    case 'marketplace':
      return buildPrompts(t, 'marketplace', [
        { id: 'marketplace-addons-available', leafKey: 'addonsAvailable' },
        {
          id: 'marketplace-email-security-benefits',
          leafKey: 'emailSecurityBenefits',
        },
        {
          id: 'marketplace-password-manager-benefits',
          leafKey: 'passwordManagerBenefits',
        },
        {
          id: 'marketplace-antivirus-benefits',
          leafKey: 'antivirusBenefits',
        },
        { id: 'marketplace-compare-pricing', leafKey: 'comparePricing' },
        { id: 'marketplace-plan-includes', leafKey: 'planIncludes' },
        { id: 'marketplace-vpn-benefits', leafKey: 'vpnBenefits' },
      ]);
    case 'track_orders':
      return buildPrompts(t, 'trackOrders', [
        { id: 'track-orders-in-progress', leafKey: 'inProgress' },
        { id: 'track-orders-all', leafKey: 'all' },
        { id: 'track-orders-delayed', leafKey: 'delayed' },
        {
          id: 'track-orders-tracking-numbers',
          leafKey: 'trackingNumbers',
        },
        { id: 'track-orders-delivery-date', leafKey: 'deliveryDate' },
        { id: 'track-orders-spend-this-year', leafKey: 'spendThisYear' },
        { id: 'track-orders-get-help', leafKey: 'getHelp' },
      ]);
    case 'ship_laptop':
      return buildPrompts(t, 'shipLaptop', [
        { id: 'ship-laptop-how-to', leafKey: 'howTo' },
        { id: 'ship-laptop-to-employee', leafKey: 'toEmployee' },
        { id: 'ship-laptop-to-warehouse', leafKey: 'toWarehouse' },
        { id: 'ship-laptop-to-site', leafKey: 'toSite' },
        { id: 'ship-laptop-qr-codes', leafKey: 'qrCodes' },
        { id: 'ship-laptop-warehouse-fees', leafKey: 'warehouseFees' },
        { id: 'ship-laptop-charging-cable', leafKey: 'chargingCable' },
      ]);
    case 'qr_code_shipments':
      return buildPrompts(t, 'qrCodeShipments', [
        { id: 'shipments-show-all', leafKey: 'showAll' },
        { id: 'shipments-not-shipped', leafKey: 'notShipped' },
        { id: 'shipments-in-transit', leafKey: 'inTransit' },
        { id: 'shipments-expiring-soon', leafKey: 'expiringSoon' },
        { id: 'shipments-delivered', leafKey: 'delivered' },
        { id: 'shipments-track-specific', leafKey: 'trackSpecific' },
        { id: 'shipments-reminder-emails', leafKey: 'reminderEmails' },
      ]);
    case 'storefront':
      return buildPrompts(t, 'storefront', [
        {
          id: 'storefront-employees-need-devices',
          leafKey: 'employeesNeedDevices',
        },
        { id: 'storefront-company-assets', leafKey: 'companyAssets' },
        { id: 'storefront-how-to-purchase', leafKey: 'howToPurchase' },
        {
          id: 'storefront-installment-options',
          leafKey: 'installmentOptions',
        },
        { id: 'storefront-global-employees', leafKey: 'globalEmployees' },
        {
          id: 'storefront-electric-warehouse',
          leafKey: 'electricWarehouse',
        },
        {
          id: 'storefront-apple-business-manager',
          leafKey: 'appleBusinessManager',
        },
      ]);
    case 'settings_company_profile':
      return buildPrompts(t, 'settingsCompanyProfile', [
        { id: 'settings-company-profile-customer-id', leafKey: 'customerId' },
        {
          id: 'settings-company-profile-email-provider',
          leafKey: 'emailProvider',
        },
        {
          id: 'settings-company-profile-what-page-shows',
          leafKey: 'whatPageShows',
        },
        {
          id: 'settings-company-profile-copy-customer-id',
          leafKey: 'copyCustomerId',
        },
      ]);
    case 'settings_device':
      return buildPrompts(t, 'settingsDevice', [
        { id: 'settings-device-what-page-shows', leafKey: 'whatPageShows' },
        { id: 'settings-device-enable-mdm', leafKey: 'enableMdm' },
        {
          id: 'settings-device-apple-business-manager',
          leafKey: 'appleBusinessManager',
        },
        { id: 'settings-device-zero-touch', leafKey: 'zeroTouch' },
        {
          id: 'settings-device-warranty-defaults',
          leafKey: 'warrantyDefaults',
        },
        { id: 'settings-device-mdm-policies', leafKey: 'mdmPolicies' },
      ]);
    case 'settings_billing':
      return buildPrompts(t, 'settingsBilling', [
        { id: 'settings-billing-current-plan', leafKey: 'currentPlan' },
        { id: 'settings-billing-addons', leafKey: 'addons' },
        { id: 'settings-billing-update-payment', leafKey: 'updatePayment' },
        {
          id: 'settings-billing-unpaid-invoices',
          leafKey: 'unpaidInvoices',
        },
        {
          id: 'settings-billing-download-invoice',
          leafKey: 'downloadInvoice',
        },
        {
          id: 'settings-billing-devices-warehoused',
          leafKey: 'devicesWarehoused',
        },
        {
          id: 'settings-billing-license-counts',
          leafKey: 'licenseCounts',
        },
      ]);
    case 'settings_support_center':
      return buildPrompts(t, 'settingsSupportCenter', [
        {
          id: 'settings-support-category-champions',
          leafKey: 'categoryChampions',
        },
        {
          id: 'settings-support-assign-champions',
          leafKey: 'assignChampions',
        },
        {
          id: 'settings-support-categories-need-champions',
          leafKey: 'categoriesNeedChampions',
        },
        {
          id: 'settings-support-category-vs-app-champions',
          leafKey: 'categoryVsAppChampions',
        },
        {
          id: 'settings-support-current-champions',
          leafKey: 'currentChampions',
        },
      ]);
    case 'settings_integrations':
      return buildPrompts(t, 'settingsIntegrations', [
        { id: 'settings-integrations-connected', leafKey: 'connected' },
        {
          id: 'settings-integrations-should-connect',
          leafKey: 'shouldConnect',
        },
        {
          id: 'settings-integrations-what-page-shows',
          leafKey: 'whatPageShows',
        },
        { id: 'settings-integrations-connect', leafKey: 'connect' },
        { id: 'settings-integrations-available', leafKey: 'available' },
      ]);
    default:
      return [];
  }
}

const GS_STEP_IDS: Record<GettingStartedStepKey, string> = {
  connect_hr_system: 'gs-connect_hr_system',
  add_employees: 'gs-add_employees',
  invite_admins: 'gs-invite_admins',
  add_applications: 'gs-add_applications',
  connect_app_integration: 'gs-connect_app_integration',
  add_groups: 'gs-add_groups',
  enable_mdm: 'gs-enable_mdm',
  add_payment_method: 'gs-add_payment_method',
  configure_employee_support: 'gs-configure_employee_support',
  connect_abm: 'gs-connect_abm',
  configure_ztp: 'gs-configure_ztp',
  notify_team: 'gs-notify_team',
  invite_employees: 'gs-invite_employees',
};

/**
 * Returns up to 3 quick prompts for the given incomplete GS step keys, in
 * the order provided (caller is responsible for priority ordering).
 */
export function getGettingStartedQuickPrompts(
  incompleteStepKeys: GettingStartedStepKey[],
  t: TFunction<'ai-chat'>
): QuickPrompt[] {
  return incompleteStepKeys.slice(0, MAX_GS_QUICK_PROMPTS).map(key => {
    const message = t(`quickPrompts.gettingStarted.${key}`);
    return { id: GS_STEP_IDS[key], label: message, message };
  });
}

export { MAX_QUICK_PROMPTS, MAX_GS_QUICK_PROMPTS };
